import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilSquareIcon, TrashIcon, PlusIcon, UserGroupIcon, } from '@heroicons/react/24/outline';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { Spinner } from '@/components/Spinner';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { useGroups, useCompanies, useCreateGroup, useUpdateGroup, useDeleteGroup, } from '@/hooks/useApi';
import { useAuthStore } from '@/stores/auth';
import { toPersianDigits } from '@/lib/jalali';
export function GroupsManager() {
    const me = useAuthStore((s) => s.user);
    const scopedToCompany = me.role === 'SUPER_USER' ? me.companyId ?? undefined : undefined;
    const groupsQ = useGroups(scopedToCompany);
    const companiesQ = useCompanies();
    const deleteG = useDeleteGroup();
    const { confirm, node: confirmNode } = useConfirm();
    const [editing, setEditing] = useState(undefined);
    const groups = groupsQ.data || [];
    const grouped = useMemo(() => {
        const byCompany = {};
        for (const g of groups) {
            const k = g.company?.name ?? '—';
            (byCompany[k] = byCompany[k] || []).push(g);
        }
        return byCompany;
    }, [groups]);
    const handleDelete = async (g) => {
        if (await confirm({ message: `غیرفعال‌سازی گروه «${g.name}»؟`, danger: true })) {
            try {
                await deleteG.mutateAsync(g.id);
                toast.success('گروه غیرفعال شد');
            }
            catch (e) {
                toast.error(e?.response?.data?.error || 'خطا');
            }
        }
    };
    if (groupsQ.isLoading || companiesQ.isLoading)
        return _jsx(Spinner, {});
    return (_jsxs("div", { className: "space-y-4", children: [confirmNode, _jsxs("div", { className: "flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-semibold", children: "\u06AF\u0631\u0648\u0647\u200C\u0647\u0627" }), _jsxs(Button, { onClick: () => setEditing(null), children: [_jsx(PlusIcon, { className: "h-4 w-4" }), " \u06AF\u0631\u0648\u0647 \u062C\u062F\u06CC\u062F"] })] }), Object.keys(grouped).length === 0 && (_jsxs("div", { className: "rounded-xl border-2 border-dashed border-gray-200 p-10 text-center text-sm text-gray-500 dark:border-gray-800", children: [_jsx(UserGroupIcon, { className: "mx-auto mb-2 h-10 w-10 text-gray-300" }), "\u06AF\u0631\u0648\u0647\u06CC \u062B\u0628\u062A \u0646\u0634\u062F\u0647 \u0627\u0633\u062A"] })), Object.entries(grouped).map(([companyName, list]) => (_jsxs("section", { className: "overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900", children: [_jsx("header", { className: "border-b border-gray-100 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-gray-800 dark:bg-gray-800/60 dark:text-gray-200", children: companyName }), _jsx("ul", { className: "divide-y divide-gray-100 dark:divide-gray-800", children: list.map((g) => (_jsxs("li", { className: "flex items-center justify-between gap-3 px-4 py-2.5 text-sm", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { className: "flex h-8 w-8 items-center justify-center rounded-full bg-arshe-100 text-arshe-700 dark:bg-arshe-950 dark:text-arshe-300", children: _jsx(UserGroupIcon, { className: "h-4 w-4" }) }), _jsxs("div", { children: [_jsx("div", { className: "font-medium", children: g.name }), _jsxs("div", { className: "text-xs text-gray-500", children: [toPersianDigits(g._count?.users ?? 0), " \u0639\u0636\u0648"] })] })] }), _jsxs("div", { className: "flex gap-1", children: [_jsx("button", { onClick: () => setEditing(g), className: "rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800", children: _jsx(PencilSquareIcon, { className: "h-4 w-4" }) }), _jsx("button", { onClick: () => handleDelete(g), className: "rounded-md p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950", children: _jsx(TrashIcon, { className: "h-4 w-4" }) })] })] }, g.id))) })] }, companyName))), _jsx(GroupFormModal, { open: editing !== undefined, group: editing ?? null, onClose: () => setEditing(undefined), fixedCompanyId: scopedToCompany })] }));
}
function GroupFormModal({ open, onClose, group, fixedCompanyId }) {
    const { t } = useTranslation();
    const companiesQ = useCompanies();
    const create = useCreateGroup();
    const update = useUpdateGroup();
    const [name, setName] = useState('');
    const [companyId, setCompanyId] = useState(undefined);
    useState(() => undefined); // touched by react-hooks lint when re-rendered; we use useEffect below
    // Sync from incoming group
    useMemo(() => {
        if (!open)
            return;
        if (group) {
            setName(group.name);
            setCompanyId(group.companyId);
        }
        else {
            setName('');
            setCompanyId(fixedCompanyId);
        }
    }, [group, fixedCompanyId, open]);
    const submit = async () => {
        if (!name.trim()) {
            toast.error('نام گروه الزامی است');
            return;
        }
        if (!group && !companyId) {
            toast.error('شرکت را انتخاب کنید');
            return;
        }
        try {
            if (group)
                await update.mutateAsync({ id: group.id, name: name.trim() });
            else
                await create.mutateAsync({ name: name.trim(), companyId: companyId });
            toast.success(t('common.save'));
            onClose();
        }
        catch (e) {
            toast.error(e?.response?.data?.error || 'خطا');
        }
    };
    return (_jsx(Modal, { open: open, onClose: onClose, title: group ? 'ویرایش گروه' : 'گروه جدید', footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", onClick: onClose, children: t('common.cancel') }), _jsx(Button, { onClick: submit, loading: create.isPending || update.isPending, children: t('common.save') })] }), children: _jsxs("div", { className: "space-y-3", children: [_jsx(Field, { label: "\u0646\u0627\u0645 \u06AF\u0631\u0648\u0647", children: _jsx(Input, { value: name, onChange: (e) => setName(e.target.value) }) }), !group && (_jsx(Field, { label: t('common.company'), children: _jsxs(Select, { disabled: !!fixedCompanyId, value: companyId ?? '', onChange: (e) => setCompanyId(e.target.value ? Number(e.target.value) : undefined), children: [_jsx("option", { value: "", children: "\u2014" }), companiesQ.data?.map((c) => _jsx("option", { value: c.id, children: c.name }, c.id))] }) }))] }) }));
}
